import { forwardRef } from '@heroui/system';
import { BsThreeDots } from 'react-icons/bs';
import { PiChatText, PiVideoCamera } from 'react-icons/pi';
import {
  Card,
  CardProps,
  CardHeader,
  CardBody,
  CardFooter,
  Divider,
  IconButton,
  TmdxChip,
  Button,
  IconButtonProps,
  CustomChipProps,
  Tooltip,
} from '@ds';
import {
  TmdxUser,
  CustomUserProps,
  ResourceAvatarProps,
  ChipWithStatusProps,
  DropdownActionMenu,
  NestedDropdownProps,
  NestedDropdownItem,
} from '@ui';
import { UsersIcon } from '@icons';
import { Key, memo, ReactNode, useCallback } from 'react';

const DEFAULT_MENU_PROPS: Partial<NestedDropdownProps> = {};

const DEFAULT_CHIP_PROPS: CustomChipProps = {
  variant: 'dot',
  size: 'xxs',
  color: 'secondary',
};

export type ResourceItemType = NestedDropdownItem<{
  type?: string;
  to?: string;
  onUserMenuPress?: (type?: string, to?: string) => void;
}>;

export type CustomCardProps = CardProps & {
  list: ResourceItemType[];
  icon?: ReactNode;
  type: 'user' | 'empty' | 'device';
  size?: 'lg' | 'sm';
  title: CustomUserProps['title'];
  subTitle?: CustomUserProps['description'];
  phoneNumber?: string | null;
  emptyContentText?: string;
  assignBtnText?: string;
  label?: string;
  isReadOnly?: boolean;
  buttonProps?: IconButtonProps;
  avatarProps?: ResourceAvatarProps;
  chipProps?: ChipWithStatusProps;
  chipText?: string;
  menuDisabled?: boolean;
  menuProps?: Partial<NestedDropdownProps>;
  cardClassNames?: string;
  showChip?: boolean;
  truncated?: boolean;
  onUserMenuPress?: (type: string, to: string) => void;
  onChatClick?: () => void;
  onVideoClick?: () => void;
  onAssignClick?: () => void;
  onDotsClick?: () => void;
};

const TmdxResourceCardBase = forwardRef<'div', CustomCardProps>((props, ref) => {
  const {
    icon,
    list,
    type,
    size = 'lg',
    title,
    subTitle,
    phoneNumber = '',
    emptyContentText,
    isReadOnly,
    assignBtnText,
    label,
    avatarProps,
    buttonProps,
    chipProps,
    menuDisabled,
    chipText,
    cardClassNames = '',
    menuProps = DEFAULT_MENU_PROPS,
    showChip = true,
    truncated = true,
    onUserMenuPress,
    onChatClick,
    onVideoClick,
    onAssignClick,
    onDotsClick,
    ...restProps
  } = props;

  const cardProps: CardProps = restProps;

  const isSizeSm = size === 'sm';
  const isSizeLg = size === 'lg';
  const isTypeDevice = type === 'device';

  const getCardPaddingStyle = () => {
    if (type === 'empty') return 'pb-4';
    if (isSizeSm) return 'pt-2 pb-2';
    if (isSizeLg && type === 'user') return 'pt-4 pb-0';
    if (isSizeLg && type === 'device') return 'pt-4 pb-4';
    return 'pb-0';
  };

  const getCardHeaderPadding = () => {
    if (isSizeSm || type === 'device') return 'pb-0';
    return 'pb-3';
  };

  const getCardBg = () => {
    return isSizeLg ? 'bg-base-80' : 'bg-white';
  };

  const getAvatarSize = () => {
    if (isSizeSm && type === 'device') return 'xl';
    if (!isSizeSm && type === 'device') return 'xxl';
    return 'xl';
  };

  const cardClass = `inline-flex p-4 w-full shadow-none rounded-2xl ${cardClassNames} ${getCardBg()} ${getCardPaddingStyle()}`;

  const handleAction = useCallback(
    (key: Key) => {
      const itemsWithActions = list.flatMap((l) => (l.children?.length ? l.children : l));
      const item = itemsWithActions.find((i) => i.key === key);

      if (item) {
        item.onUserMenuPress?.(item.type, item.to);
      }
    },
    [list],
  );

  const renderHeader = () => (
    <CardHeader className={`flex p-0 ${getCardHeaderPadding()}`}>
      <div
        className={`flex justify-between ${isSizeLg ? 'items-start' : 'items-center'} w-full ${!isTypeDevice ? 'relative' : ''}`}
      >
        <TmdxUser
          title={title}
          description={subTitle}
          titleSize="lg"
          descriptionSize={isSizeSm ? 'md' : 'lg'}
          titleColor="black"
          avatarProps={{
            size: getAvatarSize(),
            radius: 'xl',
            name: title,
            ...avatarProps,
          }}
          showIcon={!!icon}
          icon={icon}
          truncated={truncated}
        >
          {showChip && isTypeDevice && isSizeLg && (
            <TmdxChip className="mt-2.5" {...DEFAULT_CHIP_PROPS} {...chipProps}>
              {chipText || ''}
            </TmdxChip>
          )}
        </TmdxUser>
        {showChip && !isSizeLg && (
          <Tooltip placement="right" className="p-0" content={<span className="px-2">{chipText}</span>}>
            <TmdxChip {...DEFAULT_CHIP_PROPS} {...chipProps}>
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap max-w-[80px]">
                {chipText || ''}
              </span>
            </TmdxChip>
          </Tooltip>
        )}
        {showChip && isSizeLg && !isTypeDevice && (
          <Tooltip placement="right" className="p-0" content={<span className="px-2">{chipText}</span>}>
            <TmdxChip className="absolute right-0 bottom-[2px]" {...DEFAULT_CHIP_PROPS} {...chipProps}>
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap max-w-[80px]">
                {chipText || ''}
              </span>
            </TmdxChip>
          </Tooltip>
        )}
        {!isSizeSm && !menuDisabled && (
          <DropdownActionMenu
            items={list}
            trigger={
              <IconButton
                aria-label="Open user menu"
                variant="light"
                size="xs"
                startContent={<BsThreeDots size={16} />}
                {...buttonProps}
              />
            }
            placement="bottom-end"
            onAction={handleAction}
            classNames={{
              content: 'min-w-50.75',
              base: 'max-w-50.75',
            }}
            {...menuProps}
          />
        )}
      </div>
    </CardHeader>
  );

  const renderEmptyContent = () => (
    <CardBody className="flex flex-1 flex-col items-center p-0">
      <UsersIcon className="text-base-30 size-8" />
      <p className="text-m-m text-base-30 mb-3">{emptyContentText}</p>
      <Button size="sm" radius="full" className="min-w-25" onClick={onAssignClick}>
        {assignBtnText}
      </Button>
    </CardBody>
  );

  const renderFooter = () => (
    <>
      <Divider className="bg-base-55" />
      <CardFooter className="flex flex-row justify-between px-0 pb-3 pt-2">
        <div className="text-m-r h-5">{phoneNumber}</div>
        <div className="flex gap-4">
          {onChatClick && (
            <IconButton
              aria-label="Start chat"
              variant="action"
              size="xs-m"
              startContent={<PiChatText size={24} />}
              onPress={onChatClick}
              {...buttonProps}
            />
          )}
          {onVideoClick && (
            <IconButton
              aria-label="Start video call"
              variant="action"
              size="xs-m"
              startContent={<PiVideoCamera size={24} />}
              onPress={onVideoClick}
              {...buttonProps}
            />
          )}
        </div>
      </CardFooter>
    </>
  );

  return (
    <Card ref={ref} className={cardClass} {...cardProps}>
      {(type === 'user' || type === 'device') && renderHeader()}
      {type === 'empty' && renderEmptyContent()}
      {type === 'user' && isSizeLg && (phoneNumber || onChatClick || onVideoClick) && renderFooter()}
    </Card>
  );
});

export const TmdxResourceCard = memo(TmdxResourceCardBase);
